import type { ReduxAction, ThunkDispatchType } from 'app/redux/types';

import { API, getAsyncThunk } from 'app/api';
import { PrematchSignature } from 'app/constants/matchGridStakes';
import type { BackendEntities } from 'app/lib/betting/betting-entities';
import type { SubscribeAction, SubscribeActionWithArgs } from 'app/lib/signalr';
import type { SubscriptionsExtArgs } from 'app/lib/signalr/hub/main-hub';
import { addBetFactory } from 'app/modules/BetSlip/actions/addBetFactory';
import { matches, objects, sports } from 'app/modules/PrematchWS/selectors/matchGridEntities';

export const PREMATCH_MAIN_LINE_TOPIC = 'pre-match-main-line';
export const PREMATCH_MATCHES_TOPIC = 'pre-match-matches';

export const SUBSCRIBE = 'PREMATCH:SUBSCRIBE';
export const UNSUBSCRIBE = 'PREMATCH:UNSUBSCRIBE';
export const APPLY_STATE = 'PREMATCH:APPLY_STATE';
export const SUBSCRIBE_TO_PREMATCH_MATCHES = 'PREMATCH:SUBSCRIBE_TO_MATCHES';
export const UNSUBSCRIBE_FROM_PREMATCH_MATCHES = 'PREMATCH:UNSUBSCRIBE_FROM_MATCHES';
export const APPLY_PREMATCH_MATCHES = 'PREMATCH:APPLY_PREMATCH_MATCHES';
export const CLEAR_PREMATCH_MATCHES = 'PREMATCH:CLEAR_PREMATCH_MATCHES';

export type PrematchSubscribe = SubscribeAction<typeof SUBSCRIBE, {
  signature: string;
  topic: string;
}>;

export type PrematchUnsubscribe = SubscribeAction<typeof UNSUBSCRIBE>;

export type PrematchActionTypes = PrematchSubscribe | PrematchUnsubscribe;

export const subscribe = (): PrematchActionTypes => ({
  type: SUBSCRIBE,
  payload: {
    topic: PREMATCH_MAIN_LINE_TOPIC,
    signature: PrematchSignature.SUBSCRIBE_SIGNATURE,
  },
});

export const unsubscribe = (): PrematchActionTypes => ({
  type: UNSUBSCRIBE,
  payload: {
    topic: PREMATCH_MAIN_LINE_TOPIC,
  },
});

export const applyPrematchState = (payload: BackendEntities) => ({
  type: APPLY_STATE,
  payload,
});

// ---

export type PrematchSubscribeToMatches = SubscribeActionWithArgs<
  typeof SUBSCRIBE_TO_PREMATCH_MATCHES,
  Pick<SubscriptionsExtArgs, 'Matches'>
>;

export type PrematchUnsubscribeFromMatches = SubscribeAction<
  typeof UNSUBSCRIBE_FROM_PREMATCH_MATCHES
>;

export type ApplyPrematchMatches = ReduxAction<
  typeof APPLY_PREMATCH_MATCHES,
  BackendEntities
>;

export const subscribeToPrematchMatches = (
  topic: string,
  matchIds: number[],
): PrematchSubscribeToMatches => ({
  type: SUBSCRIBE_TO_PREMATCH_MATCHES,
  args: {
    Matches: matchIds,
  },
  payload: {
    topic: `${PREMATCH_MATCHES_TOPIC}#${topic}`,
  },
});

export const unsubscribeFromPrematchMatches = (topic: string): PrematchUnsubscribeFromMatches => ({
  type: UNSUBSCRIBE_FROM_PREMATCH_MATCHES,
  payload: {
    topic: `${PREMATCH_MATCHES_TOPIC}#${topic}`,
  },
});

export const applyPrematchMatches = (payload: BackendEntities): ApplyPrematchMatches => ({
  type: APPLY_PREMATCH_MATCHES,
  payload,
});

export const clearPrematchMatches = () => ({
  type: CLEAR_PREMATCH_MATCHES,
});

export const SET_MATCHES_CONTAINER_HEIGHT = 'SET_MATCHES_CONTAINER_HEIGHT';

export type SetMatchesContainerHeightType = ReduxAction<
  typeof SET_MATCHES_CONTAINER_HEIGHT, { height: null | number }>;

export const setMatchesContainerHeight = (
  height: null | number,
): SetMatchesContainerHeightType => ({
  type: SET_MATCHES_CONTAINER_HEIGHT,
  payload: { height },
});

export const getPrematchContent = getAsyncThunk(
  'MATCHES:FETCH_PREMATCH_CONTENT',
  API.matches.getPrematchContent,
);

export const getFullMatchContent = getAsyncThunk(
  'MATCHES:FETCH_FULL_MATCH_CONTENT',
  API.matches.getPrematchContent,
);

type QueryPayload = {
  signature: string;
  matchId: number;
};

export const fetchFullMatchContent = (payload: QueryPayload) => {
  return (dispatch: ThunkDispatchType) => {
    dispatch(getFullMatchContent({
      signature: payload.signature,
      query: { category: 'match', id: payload.matchId },
    }));
  };
};

export const addBet = addBetFactory(matches, sports, objects);
